import React, { Fragment, Reducer, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, ButtonGroup, Snackbar } from '@material-ui/core'
import { HeaderBlock } from 'components/header-block/HeaderBlock'
import PageContainer from 'components/page-container/PageContainer'
import { getContentType } from 'state/constants/ContentType'
import SimpleTable from 'components/table/Table'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FixMeLater } from 'types/FixMeLaterType'
import { Action } from 'redux'
import { RootState } from 'state/reducers/AppReducer'
import { clearCurrentDashboardLayout, deleteDashboardLayout, getCurrentDashboardLayout, getDashboardLayouts, setDashboardLayout } from 'state/actions/DashboardLayoutActions'
import { Loading } from 'components/loading/Loading'
import JSONEditor from 'components/json-editor/JSONEditor'
import { makeStyles } from '@material-ui/styles'
import CustomizedSnackbars from 'components/snackbar/Snackbar'
import { DeleteModal } from 'containers/delete-modal/DeleteModal'
import AlertDialog from 'components/alert-dialog/AlertDialog'

interface Props {
    history: FixMeLater,
    match: FixMeLater
}

const useStyles = makeStyles({
    JSONEditor: {
        height: "100%",
        width: "100%",
        overflow: "scroll"
    }
})

function ViewDashboardLayoutPage(props: Props) {

    const currentDashboardLayout = useSelector<RootState, FixMeLater>(state => state.DashboardLayoutReducer.currentDashboardLayout)

    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()
    const classes = useStyles()

    const [loading, setLoading] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [dashboardLayoutModal, setDashboardLayoutModal] = useState(false)
    const [errors, setErrors] = useState<string[]>([])
    const [deleteLayoutAlertShowing, setDeleteLayoutAlertShowing] = useState(false)

    useEffect(() => {
        dispatch(clearCurrentDashboardLayout(() => { }))
    }, [props.match.params.dashboardLayoutId])

    useEffect(() => {
        const dashboardLayoutId = props.match.params.dashboardLayoutId
        setLoading(true)
        dispatch(getCurrentDashboardLayout(dashboardLayoutId, () => {
            setLoading(false)
        }))

        return () => {
            dispatch(clearCurrentDashboardLayout(() => { }))
        }
    }, [])

    function handleEditSaveDashboardLayout() {
        if (isEditing) {
            setDashboardLayoutModal(true)
        } else {
            setIsEditing(true)
        }
    }

    function handleDataUpdate(jsonString: string) {

        try {
            var data: object = JSON.parse(jsonString)

            dispatch(setDashboardLayout(data, (success: boolean) => {
                setDashboardLayoutModal(false)
                if (success) {
                    setIsEditing(false)
                }
            }))

        } catch (e) {
            setErrors([t('dashboard-layout.json-data-malformed')])
        }
    }

    function handleDeleteDashboardLayout() {
        if (currentDashboardLayout?.id != null) {
            setDeleteLayoutAlertShowing(false)
            dispatch(deleteDashboardLayout(currentDashboardLayout.id, (success: boolean) => {
                if (success) {
                    props.history.push("/dashboard-layouts")
                }
            }))
        }
    }

    function renderContent() {
        const stringData = JSON.stringify(currentDashboardLayout || {}, null, 4)
        return (
            <Box className={classes.JSONEditor}>
                <JSONEditor
                    data={stringData}
                    dialogOpen={dashboardLayoutModal}
                    dialogTitle={t('dashboard-layout.overriding-layout-title')}
                    dialogMessage={t('dashboard-layout.overriding-layout-description')}
                    isEditing={isEditing}
                    handleCloseDialog={() => {
                        setIsEditing(false)
                        setDashboardLayoutModal(false)
                    }}
                    handleUpdate={handleDataUpdate}
                />
            </Box>
        )
    }

    function renderDeleteModal() {
        return (
            <AlertDialog
                title={t('dashboard-layout.remove-question')}
                description={t('common.remove-description')}
                options={[
                    {
                        label: t('common.cancel'),
                        action: () => setDeleteLayoutAlertShowing(false)
                    },
                    { label: t('common.delete'), action: () => handleDeleteDashboardLayout() }
                ]}
                isOpen={deleteLayoutAlertShowing}
            />
        )
    }

    return (
        <Box>
            <HeaderBlock
                title={currentDashboardLayout?.id}
                right={
                    <ButtonGroup>
                        <Button
                            disabled={!isEditing}
                            variant="outlined"
                            color="primary"
                            onClick={() => setDeleteLayoutAlertShowing(true)}
                        >
                            {t("common.delete")}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleEditSaveDashboardLayout}
                        >
                            {
                                isEditing ?
                                    t('common.save') :
                                    t('common.edit')
                            }
                        </Button>
                        {
                            isEditing ?
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setIsEditing(false)}
                                >
                                    {t('common.cancel')}
                                </Button> :
                                null
                        }
                    </ButtonGroup>
                }
            />

            < CustomizedSnackbars
                handleClose={() => setErrors([])}
                open={errors.length > 0}
                variant="error"
                message={errors[0]}
            />

            {renderDeleteModal()}

            < PageContainer >
                {loading ? <Loading /> : renderContent()}
            </PageContainer >
        </Box >
    )
}

ViewDashboardLayoutPage.propTypes = {

}

export default ViewDashboardLayoutPage

